/**
 * What the panel's Disclaimer settings change, kept in the URL beside the panel's states: `aiRender2Disclaimer=off`
 * and `aiDisclaimerText=…`. Prototype chrome only.
 */
export interface AiRenderSettings {
    /** The render numbers (1-based) whose disclaimer is hidden; every other render shows it. */
    readonly hidden: ReadonlyArray<number>;
    /** The line every render and the fullscreen popup show; null is the default copy, empty hides it everywhere. */
    readonly text: string | null;
}

export const EMPTY_RENDER_SETTINGS: AiRenderSettings = { hidden: [], text: null };

/** One-click disclaimer texts in the panel's Disclaimer settings, as rich text; Design is the copy the markup ships, stored as null. */
export const DISCLAIMER_SAMPLES: Readonly<Record<'rich' | 'long', string>> = {
    rich: '<b>AI-sfeerimpressie</b>, kan <i>afwijken</i> van de geconfigureerde tafel',
    long: 'AI-sfeerimpressie: dit beeld is met kunstmatige intelligentie gemaakt en kan in kleur, houtnerf, afmetingen en details afwijken van de geconfigureerde tafel en van het product dat je thuis ontvangt',
};

/**
 * The disclaimer in the other shop languages, as rich text; Dutch is the copy the markup ships.
 * TODO: drafts — replace with Pascal's translations.
 */
export const DISCLAIMER_TRANSLATIONS: Readonly<Record<'de' | 'fr', string>> = {
    de: 'KI-Stimmungsbild, kann vom konfigurierten Tisch abweichen',
    fr: "Visuel d'ambiance généré par IA, peut différer de la table configurée",
};

const RENDER_COUNT = 5;
const TEXT_PARAM = 'aiDisclaimerText';

export class AiRenderUrl {
    public static read(search: string): AiRenderSettings {
        const params = new URLSearchParams(search);
        const hidden: number[] = [];
        for (let render = 1; render <= RENDER_COUNT; render++) {
            if (params.get(AiRenderUrl.disclaimerParam(render)) === 'off') {
                hidden.push(render);
            }
        }

        return { hidden, text: params.get(TEXT_PARAM) };
    }

    /** `search` with the settings' parameters set and every other parameter kept; starts with `?`, or is empty. */
    public static write(settings: AiRenderSettings, search: string): string {
        const params = new URLSearchParams(search);
        for (let render = 1; render <= RENDER_COUNT; render++) {
            if (settings.hidden.includes(render)) {
                params.set(AiRenderUrl.disclaimerParam(render), 'off');
            } else {
                params.delete(AiRenderUrl.disclaimerParam(render));
            }
        }

        if (settings.text === null) {
            params.delete(TEXT_PARAM);
        } else {
            params.set(TEXT_PARAM, settings.text);
        }

        const query = params.toString();
        return query ? `?${query}` : '';
    }

    private static disclaimerParam(render: number): string {
        return `aiRender${render}Disclaimer`;
    }
}
